import React, { useState } from 'react';
import { Send, Building, Mail, Phone, User, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

export const PartnerSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    location: '',
    email: '',
    phone: '',
    space: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.company || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Create WhatsApp message
    const message = `Hello! I'm interested in partnering with Balanced Bites Cafe.

*Name:* ${formData.name}
*Company:* ${formData.company}
*Location:* ${formData.location}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Available Space:* ${formData.space}

Looking forward to discussing partnership opportunities!`;

    const whatsappNumber = '919560209663'; // Replace with actual number
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');

    // Show success message
    toast({
      title: "Redirecting to WhatsApp",
      description: "We'll connect with you shortly!",
    });

    // Reset form
    setFormData({
      name: '',
      company: '',
      location: '',
      email: '',
      phone: '',
      space: '',
    });
  };

  return (
    <section id="partner" className="py-24 bg-gradient-to-br from-[#EBEBD3]/30 via-white to-[#EBEBD3]/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#59642C] mb-6">
              Partner With Us
            </h2>
            <div className="w-24 h-1 bg-[#FE9E1C] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bring Balanced Bites Cafe to your workplace and offer your team the food they deserve
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Partnership Benefits */}
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#EBEBD3]">
                <h3 className="text-2xl font-bold text-[#59642C] mb-6">
                  Partnership Opportunities For
                </h3>
                <div className="space-y-4">
                  {[
                    'Corporate parks and office campuses',
                    'Hospitals and healthcare facilities',
                    'Commercial developers and property managers',
                    'Airport terminals and transit hubs',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-[#FE9E1C] rounded-full flex items-center justify-center mt-0.5">
                        <span className="text-white text-sm font-bold">✓</span>
                      </div>
                      <p className="text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#59642C] to-[#59642C]/90 p-8 rounded-3xl text-white shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Why Partner With Us?</h3>
                <ul className="space-y-3 text-white/90">
                  <li className="flex items-start space-x-2">
                    <span className="text-[#FE9E1C]">•</span>
                    <span>Premium food experience for your community</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#FE9E1C]">•</span>
                    <span>Minimal space requirements</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#FE9E1C]">•</span>
                    <span>Professionally managed operations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#FE9E1C]">•</span>
                    <span>Attractive revenue-sharing model</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Partnership Form */}
            <div className="bg-white p-8 lg:p-10 rounded-3xl shadow-xl border border-[#EBEBD3]">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="pl-12 h-12 border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Company *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company name"
                      className="pl-12 h-12 border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City or area"
                      className="pl-12 h-12 border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@company.com"
                      className="pl-12 h-12 border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Phone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="pl-12 h-12 border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#59642C] mb-2">
                    Available Space for Kiosk
                  </label>
                  <Textarea
                    name="space"
                    value={formData.space}
                    onChange={handleChange}
                    placeholder="Describe the available space (dimensions, location within property, foot traffic, etc.)"
                    className="border-[#EBEBD3] focus:border-[#59642C] focus:ring-[#59642C] min-h-24"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#FE9E1C] hover:bg-[#e68a0f] text-white h-12 text-lg font-semibold group transition-all duration-200 hover:scale-105"
                >
                  Partner With Us
                  <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  * Required fields. You'll be redirected to WhatsApp to complete your inquiry.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
